import { useEffect, useState } from "react";
import { Loader2, Trophy, Award, ListChecks, Wallet, TrendingUp, TrendingDown, AlertTriangle, MapPin, Gauge } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import api from "@/lib/api";

const eur = (n) => `${n < 0 ? "-" : ""}${Math.abs(n).toFixed(2).replace(".", ",")} €`;

const CARD = "rounded-3xl border border-[#00E676]/15 bg-[#0F1812] transition-all duration-500 hover:border-[#00E676]/30 hover:shadow-[0_8px_32px_rgba(0,230,118,0.05)]";
const RISE = "animate-in fade-in slide-in-from-bottom-6 duration-700 fill-mode-both";

const Kpi = ({ icon: Icon, label, value, sub, subTone = "green", delay }) => {
  const toneCls = subTone === "gold"
    ? "bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/20"
    : "bg-[#00E676]/10 text-[#39FF14] border-[#00E676]/20";
  return (
    <div className={`${CARD} ${RISE} p-6 lg:p-8 hover:-translate-y-1`} style={{ animationDelay: delay }}>
      <div className="flex items-start justify-between">
        <span className="p-3 rounded-2xl bg-gradient-to-br from-[#00E676]/20 to-[#00E676]/5 border border-[#00E676]/20 text-[#00E676]">
          <Icon size={22} />
        </span>
        {sub && <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold border ${toneCls}`}>{sub}</span>}
      </div>
      <div className="tabular text-4xl lg:text-5xl font-light tracking-tight text-white mt-6">{value}</div>
      <div className="text-xs md:text-sm font-medium text-[#A1B5A8] uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
};

const ChartDefs = () => (
  <defs>
    <linearGradient id="greenBar" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#39FF14" />
      <stop offset="100%" stopColor="#00E676" stopOpacity={0.55} />
    </linearGradient>
    <linearGradient id="goldLine" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#FFD700" />
      <stop offset="100%" stopColor="#FFB300" />
    </linearGradient>
  </defs>
);

const tooltipStyle = { background: "#0F1812", border: "1px solid rgba(0,230,118,0.25)", borderRadius: 12, color: "#fff", boxShadow: "0 10px 40px rgba(0,0,0,0.5)" };

function Bankroll({ bk }) {
  const positive = bk.net >= 0;
  return (
    <div className={`${RISE} relative overflow-hidden flex flex-col rounded-3xl border border-[#FFD700]/20 bg-gradient-to-b from-[#0F1812] to-[#152319] p-6 lg:p-8`} style={{ animationDelay: "150ms" }} data-testid="bankroll-card">
      <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-[#FFD700]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="flex items-center gap-2 relative">
        <span className="p-2.5 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#FFD700]/5 border border-[#FFD700]/20 text-[#FFD700]"><Wallet size={18} /></span>
        <h2 className="text-lg font-semibold text-white">Bilan à 1 € par pari</h2>
      </div>
      <p className="text-xs text-[#A1B5A8] mt-3 relative">
        Bilan cumulé de tous les pronostics réglés.
      </p>

      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#FFD700]/20 to-transparent my-6" />

      <div className="grid grid-cols-2 gap-4 relative">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#697C6F]">Total misé</div>
          <div className="tabular text-2xl font-light text-white mt-1" data-testid="bankroll-stake">{eur(bk.stake)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#697C6F]">Total récupéré</div>
          <div className="tabular text-2xl font-light text-white mt-1" data-testid="bankroll-returned">{eur(bk.returned)}</div>
        </div>
      </div>

      <div className={`mt-6 rounded-2xl border p-5 relative ${positive ? "border-[#39FF14]/30 bg-[#00E676]/10" : "border-[#FF3B30]/30 bg-[#FF3B30]/10"}`}>
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#697C6F]">
          {positive ? <TrendingUp size={13} className="text-[#39FF14]" /> : <TrendingDown size={13} className="text-[#FF3B30]" />}
          Cumul (bénéfice / perte)
        </div>
        <div
          className={`tabular text-4xl font-light mt-1 ${positive ? "text-[#39FF14] drop-shadow-[0_0_12px_rgba(57,255,20,0.25)]" : "text-[#FF3B30] drop-shadow-[0_0_12px_rgba(255,59,48,0.25)]"}`}
          data-testid="bankroll-net"
        >
          {positive ? "+" : ""}{eur(bk.net)}
        </div>
      </div>

      {bk.missing_rapports > 0 && (
        <div className="mt-4 text-xs text-[#FFD700]/80 flex items-start gap-2 p-3 bg-[#FFD700]/5 rounded-xl border border-[#FFD700]/10 relative" data-testid="bankroll-missing">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{bk.missing_rapports} pari(s) gagnant(s)/placé(s) sans rapport PMU saisi ne sont pas encore comptabilisés.</span>
        </div>
      )}
    </div>
  );
}

export default function Statistics() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [authorId, setAuthorId] = useState("");

  useEffect(() => {
    api.get("/stats", { params: authorId ? { author_id: authorId } : {} }).then((r) => {
      setStats(r.data);
      if (r.data.members?.length) setMembers(r.data.members);
    });
  }, [authorId]);

  if (!stats)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#00E676]" /></div>;

  const g = stats.general;
  const chartData = stats.by_month.map((m) => ({
    name: new Date(m.month + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
    taux: m.win_rate,
  }));

  let run = 0;
  const bankChart = (stats.bankroll_by_month || []).map((m) => {
    run = Math.round((run + m.net) * 100) / 100;
    return { name: new Date(m.month + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }), cumul: run };
  });

  return (
    <div className="space-y-10">
      <div className="mb-2">
        <div className="text-[#39FF14] font-script text-2xl">Transparence totale</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Statistiques</h1>
        <p className="text-[#A1B5A8] mt-1 max-w-2xl">Nos performances détaillées, sans filtre.</p>

        <div className="flex flex-wrap items-center gap-2.5 mt-6" data-testid="stats-members-pills">
          <button
            onClick={() => setAuthorId("")}
            data-testid="stats-pill-team"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap border ${
              authorId === ""
                ? "bg-[#00E676] text-[#04180a] border-[#00E676] shadow-[0_0_15px_rgba(0,230,118,0.3)]"
                : "bg-[#0F1812]/60 text-[#A1B5A8] border-[#00E676]/15 hover:border-[#00E676]/40 hover:text-white"
            }`}
          >
            L'équipe A Nous Turf
          </button>
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => setAuthorId(m.id)}
              data-testid={`stats-pill-${m.id}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap border ${
                authorId === m.id
                  ? "bg-[#00E676] text-[#04180a] border-[#00E676] shadow-[0_0_15px_rgba(0,230,118,0.3)]"
                  : "bg-[#0F1812]/60 text-[#A1B5A8] border-[#00E676]/15 hover:border-[#00E676]/40 hover:text-white"
              }`}
            >
              {m.name}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Kpi icon={ListChecks} label="Nombre de pronostics" value={g.total} delay="0ms" />
        <Kpi icon={Trophy} label="Pronostics gagnants" value={`${g.wins}/${g.win_denom}`} sub={`${g.win_rate}% de victoires`} delay="80ms" />
        <Kpi icon={Award} label="Pronostics placés" value={`${g.places}/${g.place_denom}`} sub={`${g.place_rate}% placés`} delay="160ms" />
      </div>

      {/* Bilan + Evolution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {stats.bankroll && <div className="lg:col-span-1"><Bankroll bk={stats.bankroll} /></div>}
        {bankChart.length > 0 && (
          <div className={`${CARD} ${RISE} lg:col-span-2 p-6 lg:p-8`} style={{ animationDelay: "200ms" }} data-testid="bankroll-chart">
            <h2 className="text-lg font-semibold text-white">Évolution du bilan</h2>
            <p className="text-xs text-[#A1B5A8] mt-1 mb-4">Cumul du bénéfice / perte sur les 12 derniers mois (à 1 € par pari).</p>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bankChart} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <ChartDefs />
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(105,124,111,0.12)" vertical={false} />
                <XAxis dataKey="name" stroke="#697C6F" fontSize={11} tickLine={false} axisLine={{ stroke: "#2d3b32" }} />
                <YAxis stroke="#697C6F" fontSize={11} unit=" €" tickLine={false} axisLine={false} width={52} />
                <ReferenceLine y={0} stroke="#697C6F" strokeDasharray="4 4" />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "rgba(255,215,0,0.25)" }} formatter={(v) => [eur(v), "Cumul"]} />
                <Line type="monotone" dataKey="cumul" name="cumul" stroke="url(#goldLine)" strokeWidth={2.5} dot={{ r: 3, fill: "#FFD700", strokeWidth: 0 }} activeDot={{ r: 5, fill: "#FFD700" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Réussite par mois + hippodrome */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className={`${CARD} ${RISE} p-6 lg:p-8`} style={{ animationDelay: "240ms" }}>
          <h2 className="text-lg font-semibold text-white mb-4">Réussite par mois</h2>
          {chartData.length === 0 ? (
            <p className="text-[#697C6F] text-sm py-12 text-center">Pas encore de données.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <ChartDefs />
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(105,124,111,0.12)" vertical={false} />
                <XAxis dataKey="name" stroke="#697C6F" fontSize={11} tickLine={false} axisLine={{ stroke: "#2d3b32" }} />
                <YAxis stroke="#697C6F" fontSize={11} unit="%" tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,230,118,0.06)" }} formatter={(v) => [`${v}%`, "Réussite"]} />
                <Bar dataKey="taux" fill="url(#greenBar)" radius={[8, 8, 0, 0]} maxBarSize={46} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`${CARD} ${RISE} p-6 lg:p-8`} style={{ animationDelay: "280ms" }}>
          <h2 className="text-lg font-semibold text-white mb-5 flex items-center gap-2"><MapPin size={17} className="text-[#00E676]" /> Réussite par hippodrome</h2>
          {stats.by_hippodrome.length === 0 ? (
            <p className="text-[#697C6F] text-sm py-12 text-center">Pas encore de données.</p>
          ) : (
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1" data-testid="hippodrome-stats">
              {stats.by_hippodrome.map((h) => (
                <div key={h.hippodrome}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium text-white/90">{h.hippodrome}</span>
                    <span className="tabular text-[#A1B5A8]">{h.win_rate}% <span className="text-[#697C6F]">({h.wins}/{h.total})</span></span>
                  </div>
                  <div className="h-2.5 w-full bg-[#131F17] rounded-full overflow-hidden border border-white/5">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#00E676]/50 to-[#39FF14] transition-[width] duration-700" style={{ width: `${h.win_rate}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Réussite par discipline */}
      {stats.by_discipline && (
        <div className={`${CARD} ${RISE} p-6 lg:p-8`} style={{ animationDelay: "320ms" }} data-testid="discipline-stats">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2"><Gauge size={17} className="text-[#00E676]" /> Réussite par discipline</h2>
          <p className="text-xs text-[#A1B5A8] mb-6">Trot (monté / attelé) contre Galop (plat, haies, steeple, cross).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {stats.by_discipline.map((d) => (
              <div
                key={d.key}
                data-testid={`discipline-${d.key}`}
                className="rounded-2xl border border-[#00E676]/15 bg-[#131F17] p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold uppercase tracking-wider text-[#39FF14]">{d.key === "trot" ? "Trot" : "Galop"}</span>
                  <span className="text-[11px] text-[#697C6F]">{d.label.replace(/^(Trot|Galop)\s*/, "")}</span>
                </div>
                <div className="tabular text-4xl lg:text-5xl font-light text-white mt-4">{d.win_rate}%</div>
                <div className="text-xs text-[#A1B5A8] mt-1">{d.wins}/{d.total} réussi{d.wins > 1 ? "s" : ""} sur {d.total} prono{d.total > 1 ? "s" : ""}</div>
                <div className="mt-4 h-2.5 w-full bg-[#0F1812] rounded-full overflow-hidden border border-white/5">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#00E676]/50 to-[#39FF14] transition-[width] duration-700" style={{ width: `${d.win_rate}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
